import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Check, X, Quote, ArrowRight, Dumbbell, Brain, BookOpenCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ApplicationForm from "@/components/ApplicationForm";

const HERO_BG = "https://images.pexels.com/photos/1552242/pexels-photo-1552242.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";
const COACH_IMG = "/images/xoel_pesomuerto.JPG";
const GYM_IMG =
  "https://images.pexels.com/photos/29392546/pexels-photo-29392546.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function SectionEyebrow({ children, num }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      {num && <span className="font-mono-mtk text-[10px] text-mtk-gold/80 tracking-[0.3em]">{num}</span>}
      <span className="h-px w-8 bg-mtk-gold/60" />
      <span className="font-mono-mtk text-[10px] tracking-[0.35em] uppercase text-mtk-gold">
        {children}
      </span>
    </div>
  );
}

function H2({ children, className = "" }) {
  return (
    <h2 className={`font-heading text-3xl md:text-5xl font-black tracking-tight uppercase text-white text-balance ${className}`}>
      {children}
    </h2>
  );
}

export default function Landing() {
  const { t } = useLang();
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location.hash]);

  return (
    <div id="top" className="bg-mtk-bg text-white selection:bg-mtk-gold selection:text-mtk-bg">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center pt-24 md:pt-0 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <div className="absolute right-0 top-0 h-full w-full md:w-1/2">
            <img src="/images/xoel_portada.JPG" alt="" className="w-full h-full object-cover object-top" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-mtk-bg from-30% via-mtk-bg/90 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-mtk-bg via-transparent to-mtk-bg/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 w-full">
          <div className="max-w-4xl md:max-w-xl">
            <SectionEyebrow num="01">{t.hero.eyebrow}</SectionEyebrow>
            <h1 className="font-heading font-black tracking-[-0.02em] uppercase text-balance text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95]">
              {t.hero.title1}
              <br />
              <span className="gold-shimmer">{t.hero.title2}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a href="#apply">
                <Button
                  data-testid="hero-apply-btn"
                  className="rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.25em] text-sm h-14 px-8 group"
                >
                  {t.hero.ctaPrimary}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a href="#method">
                <Button
                  variant="outline"
                  data-testid="hero-method-btn"
                  className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/5 hover:text-mtk-gold font-bold uppercase tracking-[0.25em] text-sm h-14 px-8"
                >
                  {t.hero.ctaSecondary}
                </Button>
              </a>
            </div>
            <p className="mt-6 font-mono-mtk text-[11px] tracking-[0.3em] uppercase text-zinc-500">
              {t.hero.subcta}
            </p>

            <div className="mt-16 grid grid-cols-3 gap-3 md:gap-8 max-w-xl">
              {[
                { l: t.hero.stat1, v: t.hero.stat1v },
                { l: t.hero.stat2, v: t.hero.stat2v },
                { l: t.hero.stat3, v: t.hero.stat3v },
              ].map((s, i) => (
                <div key={i} className="border-l border-mtk-gold/30 pl-3 md:pl-5">
                  <div className="font-heading text-xl md:text-3xl font-black text-white tracking-tight">{s.v}</div>
                  <div className="font-mono-mtk text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REALIDAD ACTUAL */}
      <section id="reality" className="relative py-24 md:py-36 border-t border-mtk-border" data-testid="reality-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-5">
              <SectionEyebrow num="02">{t.reality.eyebrow}</SectionEyebrow>
              <H2>
                {t.reality.title1}
                <br />
                <span className="text-mtk-gold">{t.reality.title2}</span>
              </H2>
            </div>
            <div className="md:col-span-7 md:pt-10">
              <p className="text-zinc-300 text-lg">{t.reality.lead}</p>
              <ul className="mt-8 divide-y divide-mtk-border border-y border-mtk-border">
                {t.reality.points.map((p, i) => (
                  <li key={i} className="py-5 flex gap-5 items-start" data-testid={`reality-point-${i}`}>
                    <span className="font-mono-mtk text-[11px] text-mtk-gold/70 tracking-[0.2em] mt-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-zinc-300 text-base md:text-lg">{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 border-l-2 border-mtk-gold pl-5 md:pl-8">
                <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold/80">
                  {t.reality.worst}
                </div>
                <p className="text-white text-xl md:text-2xl font-heading font-bold mt-2 leading-snug">
                  {t.reality.worstLine}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS MTK */}
      <section id="method" className="relative py-24 md:py-36 bg-mtk-surface border-y border-mtk-border" data-testid="what-is-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionEyebrow num="03">{t.whatIs.eyebrow}</SectionEyebrow>
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <H2 className="md:col-span-8">{t.whatIs.title}</H2>
            <p className="md:col-span-4 text-zinc-400 text-base leading-relaxed md:pl-6 md:border-l border-mtk-gold/20">
              {t.whatIs.lead}
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-mtk-border">
            {t.whatIs.points.map((p, i) => (
              <div
                key={i}
                className="bg-mtk-surface p-6 md:p-8 hover:bg-mtk-elevated transition-colors group"
                data-testid={`whatis-point-${i}`}
              >
                <div className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold mb-4">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-white font-heading text-base md:text-lg font-semibold leading-snug">
                  {p}
                </p>
                <div className="mt-6 h-px w-8 bg-mtk-gold/40 group-hover:w-full transition-all duration-500" />
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <p className="font-heading text-2xl md:text-4xl font-black uppercase tracking-tight">
              <span className="text-zinc-500">{t.whatIs.footer1}</span>
              <br />
              <span className="text-white">{t.whatIs.footer2}</span>
            </p>
          </div>
        </div>
      </section>

      {/* 3 PILARES */}
      <section id="pillars" className="py-24 md:py-36" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <SectionEyebrow num="04">{t.pillars.eyebrow}</SectionEyebrow>
            <H2 className="mx-auto max-w-3xl">{t.pillars.title}</H2>
          </div>
          <div className="grid lg:grid-cols-3 gap-px bg-mtk-border border border-mtk-border">
            {[
              { key: "mindset", icon: Brain, data: t.pillars.mindset },
              { key: "training", icon: Dumbbell, data: t.pillars.training },
              { key: "knowledge", icon: BookOpenCheck, data: t.pillars.knowledge },
            ].map(({ key, icon: Icon, data }) => (
              <div
                key={key}
                className="relative bg-mtk-bg p-8 md:p-10 group hover:bg-mtk-surface transition-colors"
                data-testid={`pillar-${key}`}
              >
                <div className="flex items-start justify-between">
                  <div className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold">
                    {data.tag}
                  </div>
                  <Icon className="w-5 h-5 text-mtk-gold/70 group-hover:text-mtk-gold transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="mt-8 font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
                  {data.title}
                </h3>
                <ul className="mt-8 space-y-3">
                  {data.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-zinc-300 text-sm md:text-base">
                      <span className="text-mtk-gold mt-1">›</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10 pt-6 border-t border-mtk-border">
                  <p className="text-mtk-gold/90 font-heading text-base md:text-lg italic">
                    "{data.quote}"
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCIENCE */}
      <section className="py-24 md:py-36 bg-mtk-surface border-y border-mtk-border" data-testid="science-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <SectionEyebrow num="05">{t.science.eyebrow}</SectionEyebrow>
            <H2>{t.science.title}</H2>
            <p className="mt-6 text-zinc-400">{t.science.lead}</p>
          </div>
          <div className="md:col-span-7">
            <div className="border border-mtk-border">
              {t.science.items.map((it, i) => (
                <div
                  key={i}
                  className="flex items-center gap-6 p-5 md:p-6 border-b last:border-b-0 border-mtk-border hover:bg-mtk-elevated transition-colors"
                  data-testid={`science-item-${i}`}
                >
                  <div className="font-mono-mtk text-xs text-mtk-gold tracking-[0.2em] w-12">
                    {`R.${String(i + 1).padStart(2, "0")}`}
                  </div>
                  <div className="flex-1 text-white text-sm md:text-base">{it}</div>
                  <div className="font-mono-mtk text-[10px] text-zinc-600 tracking-[0.3em] hidden md:block">
                    ACTIVE
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 border-l-2 border-mtk-gold pl-5">
              <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold/80">
                {t.science.key}
              </div>
              <p className="text-white text-lg md:text-xl mt-2">{t.science.keyLine}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PARA QUIEN ES */}
      <section className="py-24 md:py-36" data-testid="forwho-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 max-w-3xl">
            <SectionEyebrow num="06">{t.forWho.eyebrow}</SectionEyebrow>
            <H2>{t.forWho.title}</H2>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-mtk-border border border-mtk-border">
            <div className="bg-mtk-bg p-8 md:p-12">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-mtk-gold" />
                <span className="font-mono-mtk text-[11px] tracking-[0.3em] uppercase text-mtk-gold">
                  {t.forWho.yes}
                </span>
              </div>
              <ul className="mt-8 space-y-5">
                {t.forWho.yesItems.map((it, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-200 text-base md:text-lg">
                    <span className="text-mtk-gold font-bold mt-0.5">+</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-mtk-bg p-8 md:p-12">
              <div className="flex items-center gap-3">
                <X className="w-5 h-5 text-zinc-500" />
                <span className="font-mono-mtk text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                  {t.forWho.no}
                </span>
              </div>
              <ul className="mt-8 space-y-5">
                {t.forWho.noItems.map((it, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-500 text-base md:text-lg line-through decoration-zinc-700">
                    <span className="text-zinc-600 mt-0.5">−</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CAMBIO TOTAL inflection */}
      <section className="py-24 md:py-36 relative overflow-hidden border-y border-mtk-border" data-testid="inflection-section">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <img src={GYM_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-mtk-bg/80" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
          <SectionEyebrow num="07">{t.inflection.eyebrow}</SectionEyebrow>
          <H2 className="mx-auto">{t.inflection.title}</H2>
          <p className="mt-8 text-zinc-300 text-lg max-w-2xl mx-auto">{t.inflection.lead}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3 md:gap-5">
            {t.inflection.items.map((it, i) => (
              <div
                key={i}
                className="border border-mtk-gold/30 px-5 md:px-7 py-3 md:py-4 font-mono-mtk text-xs md:text-sm tracking-[0.15em] uppercase text-white hover:bg-mtk-gold hover:text-mtk-bg transition-colors"
              >
                {it}
              </div>
            ))}
          </div>
          <p className="mt-12 font-heading text-2xl md:text-3xl font-black uppercase tracking-tight text-mtk-gold">
            {t.inflection.footer}
          </p>
        </div>
      </section>

      {/* SOBRE MI - Extended Bio */}
      <section id="coach" className="py-24 md:py-36" data-testid="coach-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-end">
            <div className="md:col-span-7 order-2 md:order-1">
              <SectionEyebrow num="08">{t.coach.eyebrow}</SectionEyebrow>
              <H2>{t.coach.title}</H2>
              <p className="mt-8 font-heading text-2xl md:text-3xl text-mtk-gold font-bold">
                {t.coach.name}
              </p>
              <p className="mt-3 font-mono-mtk text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-zinc-400 leading-relaxed">
                {t.coach.role}
              </p>
              <p className="mt-6 text-zinc-300 text-base md:text-lg leading-relaxed max-w-2xl">
                {t.coach.intro}
              </p>
            </div>
            <div className="md:col-span-5 order-1 md:order-2">
              <div className="relative aspect-[3/4] border border-mtk-border overflow-hidden grain-overlay">
                <img
                  src={COACH_IMG}
                  alt="Xoel Crespo"
                  className="w-full h-full object-cover grayscale contrast-110 hover:grayscale-0 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-mtk-bg/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                  <div>
                    <div className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold">
                      XOEL CRESPO
                    </div>
                    <div className="font-mono-mtk text-[10px] tracking-[0.3em] text-zinc-500">
                      COACH · ATHLETE
                    </div>
                  </div>
                  <div className="font-heading text-3xl font-black text-white/30">MTK</div>
                </div>
              </div>
            </div>
          </div>

          {/* Chapters timeline */}
          <div className="mt-20 md:mt-28 space-y-14 md:space-y-20">
            {t.coach.chapters.map((ch, i) => (
              <article
                key={i}
                className="grid md:grid-cols-12 gap-8 md:gap-10 border-t border-mtk-border pt-10 md:pt-12"
                data-testid={`coach-chapter-${i}`}
              >
                <header className="md:col-span-4">
                  <div className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold">
                    {ch.tag}
                  </div>
                  <h3 className="mt-3 font-heading text-2xl md:text-3xl font-black uppercase tracking-tight text-white leading-tight">
                    {ch.title}
                  </h3>
                </header>
                <div className="md:col-span-8 space-y-5">
                  {ch.paragraphs?.map((p, pi) => (
                    <p key={pi} className="text-zinc-300 text-base md:text-lg leading-relaxed">
                      {p}
                    </p>
                  ))}

                  {ch.stats && (
                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4">
                      {ch.stats.map((s, si) => (
                        <li
                          key={si}
                          className="flex items-start gap-3 text-zinc-200 text-sm md:text-base"
                        >
                          <span className="text-mtk-gold mt-1">›</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {ch.mentors && (
                    <div className="mt-6">
                      <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold/80">
                        {ch.mentorsLabel}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {ch.mentors.map((m, mi) => (
                          <span
                            key={mi}
                            className="border border-mtk-gold/30 px-3 py-1.5 text-xs text-zinc-200 font-mono-mtk tracking-[0.1em] uppercase"
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {ch.blocks && (
                    <div className="grid md:grid-cols-3 gap-px bg-mtk-border border border-mtk-border mt-4">
                      {ch.blocks.map((b, bi) => (
                        <div
                          key={bi}
                          className="bg-mtk-bg p-5 md:p-6 hover:bg-mtk-surface transition-colors"
                        >
                          <h4 className="font-heading text-base md:text-lg font-black uppercase text-white leading-tight">
                            {b.title}
                          </h4>
                          <p className="mt-1 font-mono-mtk text-[10px] tracking-[0.25em] uppercase text-mtk-gold">
                            {b.subtitle}
                          </p>
                          <ul className="mt-4 space-y-1.5">
                            {b.items.map((it, ii) => (
                              <li
                                key={ii}
                                className="flex gap-2 text-zinc-300 text-sm leading-snug"
                              >
                                <span className="text-mtk-gold mt-0.5">›</span>
                                <span>{it}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {ch.previous && (
                    <div className="mt-6">
                      <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold/80">
                        {ch.previousLabel}
                      </div>
                      <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                        {ch.previous.map((p, pi) => (
                          <li
                            key={pi}
                            className="text-zinc-400 text-sm flex gap-2"
                          >
                            <span className="text-mtk-gold/60">›</span>
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {ch.quote && (
                    <div className="border-l-2 border-mtk-gold pl-5 mt-4">
                      <p className="text-white text-lg md:text-xl font-heading font-semibold leading-snug">
                        "{ch.quote}"
                      </p>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>

          {/* Approach + Mission */}
          <div className="mt-20 md:mt-24 grid md:grid-cols-2 gap-px bg-mtk-border border border-mtk-border">
            <div className="bg-mtk-bg p-8 md:p-10" data-testid="coach-approach">
              <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold">
                {t.coach.approachTitle}
              </div>
              <p className="mt-4 text-zinc-300 text-base">{t.coach.approachIntro}</p>
              <ul className="mt-6 space-y-3">
                {t.coach.approach.map((a, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 text-white font-heading text-lg md:text-xl"
                  >
                    <span className="text-mtk-gold font-bold">+</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="relative bg-mtk-bg p-8 md:p-10 overflow-hidden"
              data-testid="coach-mission"
            >
              <div
                aria-hidden
                className="select-none pointer-events-none absolute -top-6 -right-4 font-heading font-black text-white/[0.04] text-[12rem] leading-none"
              >
                MTK
              </div>
              <div className="relative">
                <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold">
                  {t.coach.missionTitle}
                </div>
                <div className="mt-4 space-y-3">
                  {t.coach.missionLines.map((m, i) => (
                    <p
                      key={i}
                      className={`font-heading leading-snug ${
                        i === 0
                          ? "text-white text-xl md:text-2xl font-bold"
                          : "text-zinc-300 text-base md:text-lg"
                      }`}
                    >
                      {m}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 md:py-36 bg-mtk-surface border-y border-mtk-border" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <SectionEyebrow num="09">{t.services.eyebrow}</SectionEyebrow>
            <H2 className="mx-auto max-w-3xl">{t.services.title}</H2>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Mentorship - flagship */}
            <div className="relative border-2 border-mtk-gold bg-mtk-bg p-8 md:p-12 group" data-testid="service-mentorship">
              <div className="absolute -top-3 left-8 bg-mtk-gold text-mtk-bg px-3 py-1 font-mono-mtk text-[10px] tracking-[0.3em] uppercase font-bold">
                {t.services.mentorship.tag}
              </div>
              <h3 className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tight text-white">
                {t.services.mentorship.title}
              </h3>
              <p className="mt-3 text-mtk-gold font-mono-mtk text-xs tracking-[0.2em] uppercase">
                {t.services.mentorship.subtitle}
              </p>
              <ul className="mt-8 space-y-3">
                {t.services.mentorship.items.map((i, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white">
                    <Check className="w-4 h-4 text-mtk-gold mt-1 flex-shrink-0" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-zinc-400 italic">"{t.services.mentorship.tagline}"</p>
              <a href="#apply" className="block mt-8">
                <Button
                  data-testid="service-mentorship-cta"
                  className="w-full rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.2em] text-xs h-12"
                >
                  {t.services.mentorship.cta}
                </Button>
              </a>
            </div>

            {/* Personal training */}
            <div className="border border-mtk-border bg-mtk-bg p-8 md:p-12" data-testid="service-personal">
              <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold">
                {t.services.personal.tag}
              </div>
              <h3 className="mt-3 font-heading text-3xl md:text-5xl font-black uppercase tracking-tight text-white">
                {t.services.personal.title}
              </h3>
              <p className="mt-3 text-zinc-400 font-mono-mtk text-xs tracking-[0.2em] uppercase">
                {t.services.personal.subtitle}
              </p>
              <ul className="mt-8 space-y-3">
                {t.services.personal.items.map((i, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-zinc-300">
                    <span className="text-mtk-gold mt-1">›</span>
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-zinc-500 italic">"{t.services.personal.tagline}"</p>
              <a href="#apply" className="block mt-8">
                <Button
                  variant="outline"
                  data-testid="service-personal-cta"
                  className="w-full rounded-none border-white/20 bg-transparent text-white hover:bg-white/5 hover:text-mtk-gold font-bold uppercase tracking-[0.2em] text-xs h-12"
                >
                  {t.services.personal.cta}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="py-24 md:py-36" data-testid="results-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <SectionEyebrow num="10">{t.results.eyebrow}</SectionEyebrow>
            <H2 className="mx-auto max-w-3xl">{t.results.title}</H2>
          </div>
          <div className="grid md:grid-cols-5 gap-px bg-mtk-border border border-mtk-border">
            {t.results.items.map((r, i) => (
              <div
                key={i}
                className="bg-mtk-bg p-6 md:p-8 text-center hover:bg-mtk-surface transition-colors"
                data-testid={`result-${i}`}
              >
                <div className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold">
                  {`0${i + 1}`}
                </div>
                <p className="mt-5 text-white font-heading font-semibold text-base md:text-lg leading-snug">
                  {r}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-36 bg-mtk-surface border-y border-mtk-border" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <SectionEyebrow num="11">{t.testimonials.eyebrow}</SectionEyebrow>
            <H2>{t.testimonials.title}</H2>
          </div>
          <Carousel opts={{ loop: true, align: "start" }} className="relative">
            <CarouselContent className="-ml-4">
              {t.testimonials.items.map((tm, i) => (
                <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/2">
                  <div
                    className="border border-mtk-border bg-mtk-bg p-8 md:p-10 h-full flex flex-col"
                    data-testid={`testimonial-${i}`}
                  >
                    <Quote className="w-8 h-8 text-mtk-gold/70" strokeWidth={1.5} />
                    <p className="mt-6 text-white text-lg md:text-xl font-heading leading-relaxed flex-1">
                      "{tm.quote}"
                    </p>
                    <div className="mt-8 pt-6 border-t border-mtk-border">
                      <div className="font-heading font-bold text-white">{tm.name}</div>
                      <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold mt-1">
                        {tm.role}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-end gap-2 mt-6">
              <CarouselPrevious
                data-testid="testimonial-prev"
                className="static translate-y-0 rounded-none bg-transparent border border-mtk-border hover:bg-mtk-gold hover:text-mtk-bg hover:border-mtk-gold text-white h-11 w-11"
              />
              <CarouselNext
                data-testid="testimonial-next"
                className="static translate-y-0 rounded-none bg-transparent border border-mtk-border hover:bg-mtk-gold hover:text-mtk-bg hover:border-mtk-gold text-white h-11 w-11"
              />
            </div>
          </Carousel>
        </div>
      </section>

      {/* LIMITED ACCESS */}
      <section className="py-24 md:py-32" data-testid="limited-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <SectionEyebrow num="12">{t.limited.eyebrow}</SectionEyebrow>
          <H2 className="mx-auto">{t.limited.title}</H2>
          <div className="mt-12 grid sm:grid-cols-3 gap-px bg-mtk-border border border-mtk-border">
            {t.limited.points.map((p, i) => (
              <div key={i} className="bg-mtk-bg p-6 md:p-8" data-testid={`limited-point-${i}`}>
                <div className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold">
                  {String(i + 1).padStart(2, "0")} / 03
                </div>
                <p className="mt-4 text-white text-base md:text-lg font-heading">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section
        id="apply"
        className="py-24 md:py-36 bg-mtk-surface border-y border-mtk-border relative overflow-hidden"
        data-testid="apply-section"
      >
        <div
          aria-hidden
          className="select-none pointer-events-none absolute -top-10 right-0 font-heading font-black text-white/[0.03] text-[18vw] leading-none"
        >
          APPLY
        </div>
        <div className="max-w-3xl mx-auto px-6 md:px-12 relative">
          <div className="text-center mb-12">
            <SectionEyebrow num="13">{t.finalCta.eyebrow}</SectionEyebrow>
            <H2 className="mx-auto">{t.finalCta.title}</H2>
            <p className="mt-5 font-mono-mtk text-[11px] tracking-[0.3em] uppercase text-mtk-gold/80">
              {t.finalCta.subcopy}
            </p>
          </div>
          <div className="border border-mtk-border bg-mtk-bg p-6 md:p-10">
            <div className="mb-8">
              <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold mb-2">
                {t.form.eyebrow}
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-black uppercase tracking-tight">
                {t.form.title}
              </h3>
              <p className="mt-2 text-zinc-400 text-sm">{t.form.subtitle}</p>
            </div>
            <ApplicationForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
